/**
 * =====================================================
 * EVENT STATUS SYSTEM (Single Source of Truth)
 * =====================================================
 *
 * Central definition of:
 * - Status values (aligned with backend)
 * - Behaviour (capture eligibility, lifecycle stage)
 * - Default UI messaging
 *
 * All status-related logic should originate ONLY from this file.
 */

/**
 * Raw status values (must match backend exactly)
 */
export const EVENT_STATUS = Object.freeze({
  SUBMITTED: "Submitted",
  IN_PROGRESS: "In Progress",
  APPROVED: "Approved",
  REJECTED: "Rejected",
  CANCELLED: "Cancelled",
  ABANDONED: "Abandoned",
  CONCLUDED: "Concluded",
  ARCHIVED: "Archived",
});

/**
 * Status configuration
 *
 * - stage:   lifecycle grouping → preApproval | active | final
 * - canCapture: base eligibility (time window logic is separate)
 * - message: default tooltip when action unavailable
 */
export const STATUS_CONFIG = Object.freeze({
  [EVENT_STATUS.SUBMITTED]: {
    stage: "preApproval",
    canCapture: false,
    message: "Capture is only available for approved events.",
  },

  [EVENT_STATUS.IN_PROGRESS]: {
    stage: "preApproval",
    canCapture: false,
    message: "Capture is only available for approved events.",
  },

  [EVENT_STATUS.APPROVED]: {
    stage: "active",
    canCapture: true, // depends on date window logic
    message: null,
  },

  [EVENT_STATUS.REJECTED]: {
    stage: "final",
    canCapture: false,
    message: "Event was rejected.",
  },

  [EVENT_STATUS.CANCELLED]: {
    stage: "final",
    canCapture: false,
    message: "Event was cancelled.",
  },

  [EVENT_STATUS.ABANDONED]: {
    stage: "final",
    canCapture: false,
    message: "Event was abandoned.",
  },

  [EVENT_STATUS.CONCLUDED]: {
    stage: "final",
    canCapture: false,
    message: "Event has been concluded.",
  },

  [EVENT_STATUS.ARCHIVED]: {
    stage: "final",
    canCapture: false,
    message: "Event is archived (read-only).",
  },
});

/**
 * =====================================================
 * ACCESSORS (thin lookups)
 * =====================================================
 */

export const getStatusConfig = (status) => STATUS_CONFIG[status];

export const getStatusStage = (status) => STATUS_CONFIG[status]?.stage;

export const canStatusCapture = (status) => STATUS_CONFIG[status]?.canCapture === true;

export const getStatusMessage = (status) => STATUS_CONFIG[status]?.message;

/*
 * =====================================================
 * SEMANTIC HELPERS (use these instead of string checks)
 * =====================================================
 *
 * These replace:
 *    status === "Submitted"
 *    status === "In Progress" || status === "Approved"
 */

/** Exact match of submittd status */
export const isSubmitted = (s) => s === EVENT_STATUS.SUBMITTED;

/** Exact match of in-progress status */
export const isInProgress = (s) => s === EVENT_STATUS.IN_PROGRESS;

/** Exact match of approved status */
export const isApproved = (s) => s === EVENT_STATUS.APPROVED;

/** Exact match of rejected status */
export const isRejected = (s) => s === EVENT_STATUS.REJECTED;

/** Exact match of calncelled status */
export const isCancelled = (s) => s === EVENT_STATUS.CANCELLED;

/** Exact match of abandoned status */
export const isAbandoned = (s) => s === EVENT_STATUS.ABANDONED;

/** Exact match of concluded status */
export const isConcluded = (s) => s === EVENT_STATUS.CONCLUDED;

/** Exact match of archived status */
export const isArchived = (s) => s === EVENT_STATUS.ARCHIVED;


/* Common combinations (remove OR conditions) */
export const isPreApproval = (s) => s === EVENT_STATUS.SUBMITTED || s === EVENT_STATUS.IN_PROGRESS;

export const isInProgressOrApproved = (s) => s === EVENT_STATUS.IN_PROGRESS || s === EVENT_STATUS.APPROVED;


/* Stage-driven helpers (preferred for UI logic) */
export const isPreApprovalStage = (s) => getStatusStage(s) === "preApproval";

export const isActiveStage = (s) => getStatusStage(s) === "active";

export const isFinalStage = (s) => getStatusStage(s) === "final";
